import type { AvailabilityWindowDto, DepartureDto } from "@tripcart/shared";

// departureKey → spotsLeft. `null` = uncapped (unbounded availability).
export type AvailabilityMap = Map<string, number | null>;

/** `"YYYY-MM-DD"` or `"YYYY-MM-DD:<pkgId>"` → the 24h start times that actually
 *  run that day. Built from the same departures as `AvailabilityMap`, because
 *  the cart needs to re-scope its time picker when the traveller reschedules —
 *  a cart line's stored `availableTimes` describe the date it was ADDED on. */
export type AvailabilityTimesMap = Map<string, string[]>;

export type AvailabilityFetchResult =
  | { ok: true; map: AvailabilityMap; times: AvailabilityTimesMap }
  | { ok: false; error: string };

/** Times that run on `date` for this package, or `null` when the window hasn't
 *  loaded (callers fall back to the line's stored list rather than showing an
 *  empty picker). An empty array is meaningful: the package doesn't run. */
export function timesForDate(
  times: AvailabilityTimesMap | undefined,
  date: string,
  packageId?: string,
): string[] | null {
  if (!times) return null;
  const day = date.slice(0, 10);
  return (
    times.get(packageId ? `${day}:${packageId}` : day) ?? times.get(day) ?? null
  );
}

/** Normalise "09:00 AM" / "01:00 PM" or plain "HH:MM" to "HH:MM" 24-hour. */
export function to24h(time: string): string {
  const m = time.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!m) return time;
  let h = parseInt(m[1]!, 10);
  const isPm = m[3]!.toUpperCase() === "PM";
  if (isPm && h !== 12) h += 12;
  if (!isPm && h === 12) h = 0;
  return `${String(h).padStart(2, "0")}:${m[2]}`;
}

/** Build a lookup key for a departure. Matches the server-generated
 *  `DepartureDto.id` format so the cart can look up `spotsLeft` for the
 *  exact (date, time, package) combination the visitor selected:
 *    "YYYY-MM-DD"                        — date-only, no package
 *    "YYYY-MM-DD:HH:MM"                  — date-time, no package
 *    "YYYY-MM-DD:<pkgId>"                — date-only, per-package
 *    "YYYY-MM-DD:HH:MM:<pkgId>"          — date-time, per-package
 *
 *  `packageId` is appended only when present so cart lines created
 *  before the per-package fan-out (legacy / no packages configured)
 *  still resolve. */
export function departureKey(
  date: string,
  time?: string,
  packageId?: string,
): string {
  const d = date.slice(0, 10);
  const withTime = time ? `${d}:${to24h(time)}` : d;
  return packageId ? `${withTime}:${packageId}` : withTime;
}

/** Head-count for a cart line: sum of category counts × seatsPerUnit (a
 *  per-unit/group category like Cabin consumes `seatsPerUnit` seats each;
 *  lines persisted before this field default each category to 1). Pure so the
 *  cart can derive over-capacity reactively instead of imperatively in an event
 *  handler (which the React Compiler mis-memoised — TRV-M2). */
export function lineParticipantCount(item: {
  travelers: Record<string, number>;
  categorySeatsPerUnit?: Record<string, number>;
}): number {
  const seats = item.categorySeatsPerUnit;
  return Object.entries(item.travelers).reduce(
    (sum, [catId, count]) => sum + count * (seats?.[catId] ?? 1),
    0,
  );
}

/** `spotsLeft` for a cart line's exact (date, time, package) departure, or
 *  `null` when the map hasn't loaded / the departure is uncapped — the caller
 *  must treat `null` as "don't block" so a not-yet-loaded map never gates
 *  checkout. Mirrors the server `DepartureDto.id` keying (see `departureKey`). */
export function lineAvailableSpots(
  map: AvailabilityMap | undefined,
  item: { date: string; time?: string | null; packageId?: string | null },
): number | null {
  if (!map) return null;
  const keyed = map.get(
    departureKey(
      item.date,
      item.time || undefined,
      item.packageId ?? undefined,
    ),
  );
  if (keyed !== undefined) return keyed;
  return map.get(departureKey(item.date, item.time || undefined)) ?? null;
}

/**
 * Fetch all departures for an experience slug and return a map of
 * departureKey → spotsLeft so the cart can check real availability.
 */
export async function fetchAvailabilityMap(
  slug: string,
  range?: { startDate: string; endDate: string },
  signal?: AbortSignal,
): Promise<AvailabilityFetchResult> {
  try {
    // A date-bounded fetch (the cart asks per line's day) or the default
    // window (`range` omitted). The query is concatenated AFTER the trailing
    // slash `trailingSlash: true` requires — same shape as
    // `fetchAvailabilityPage`, so the bare path is never 308-redirected.
    const qs = range
      ? `?${new URLSearchParams({
          startDate: range.startDate,
          endDate: range.endDate,
        }).toString()}`
      : "";
    const res = await fetch(
      `/api/availability/${encodeURIComponent(slug)}/` + qs,
      { cache: "no-store", signal },
    );

    const body = (await res.json()) as {
      success: boolean;
      data?: { departures: DepartureDto[] };
      error?: { message: string };
    };

    if (!res.ok || !body.success) {
      return { ok: false, error: body.error?.message ?? "Unavailable" };
    }

    const departures = body.data?.departures ?? [];
    const map: AvailabilityMap = new Map(
      departures.map((d) => [
        departureKey(d.date, d.startTime, d.packageId),
        d.spotsLeft,
      ]),
    );

    // Index the same departures by day so a reschedule can offer the times
    // that run on the NEW date rather than the ones stored on the line.
    const times: AvailabilityTimesMap = new Map();
    for (const d of departures) {
      if (!d.startTime) continue;
      const day = d.date.slice(0, 10);
      for (const key of d.packageId ? [`${day}:${d.packageId}`, day] : [day]) {
        const slot = to24h(d.startTime);
        const bucket = times.get(key);
        if (bucket) {
          if (!bucket.includes(slot)) bucket.push(slot);
        } else {
          times.set(key, [slot]);
        }
      }
    }
    for (const bucket of times.values()) bucket.sort();

    return { ok: true, map, times };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : "Network error",
    };
  }
}

export interface AvailabilityPage {
  departures: DepartureDto[];
  window: AvailabilityWindowDto;
}

export type AvailabilityPageResult =
  | { ok: true; page: AvailabilityPage }
  | { ok: false; error: string };

/**
 * Fetch an availability slice for an experience slug. Returns the full
 * departures + window payload so the storefront calendar can append the new
 * departures and re-render with the latest `windowEnd` / `hasMoreAfter`
 * pagination signals.
 *
 * `range` omitted → no query string, mirroring the server's default-window
 * call (`/api/v1/experiences/${id}/availability` with no params) so the
 * initial client fetch loads the exact same window the SSR path used to.
 * `range` present → a date-bounded page (the calendar's forward pagination).
 *
 * `signal` is forwarded to `fetch` so callers can abort in-flight requests
 * (e.g. a `useEffect` cleanup on slug change / unmount).
 */
export async function fetchAvailabilityPage(
  slug: string,
  range?: { startDate: string; endDate: string },
  signal?: AbortSignal,
): Promise<AvailabilityPageResult> {
  try {
    const qs = range
      ? `?${new URLSearchParams({
          startDate: range.startDate,
          endDate: range.endDate,
        }).toString()}`
      : "";
    // The query is concatenated onto the path rather than interpolated inside
    // it, so the literal this call passes ends in the slash `trailingSlash:
    // true` requires — visible here, and to anything reading the call site.
    const res = await fetch(
      `/api/availability/${encodeURIComponent(slug)}/` + qs,
      {
        cache: "no-store",
        signal,
      },
    );

    const body = (await res.json()) as {
      success: boolean;
      data?: AvailabilityPage;
      error?: { message: string };
    };

    if (!res.ok || !body.success || !body.data) {
      return { ok: false, error: body.error?.message ?? "Unavailable" };
    }

    return { ok: true, page: body.data };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : "Network error",
    };
  }
}
